/**
 * Temperatur, Vignette und Korn — die Film-Anmutung über der Bühne.
 *
 * Diese Ebene liegt ÜBER dem gerenderten Bild und UNTER den HTML-Textboxen:
 * der Text bleibt lesbar, und die Unschärfe hinter einer Box schluckt die
 * Vignette sauber.
 *
 * Herkunft: bis 05.08.2026 lag genau diese Optik nur in der Pro-Editor-Hülle
 * (Storyforger). Vorschau und veröffentlichter Scrolly kannten sie nicht — Jans
 * Fund „Der Template-Scrolly übernimmt nicht die Pro-Einstellungen aus GLOBAL".
 * Jetzt zeichnet sie der geteilte Player, also überall dieselbe.
 *
 * Die Rechenwerte kommen aus `resolveGlobalLook` (@scrolly/post-types), damit
 * beide Seiten nicht mit zwei Formeln auseinanderlaufen können.
 */
import type { GlobalLook } from "@scrolly/post-types";

// Fraktales Rauschen als Kachel für das Korn.
const GRAIN_URI =
    "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='120'%20height='120'%3E%3Cfilter%20id='n'%3E%3CfeTurbulence%20type='fractalNoise'%20baseFrequency='0.85'%20numOctaves='2'%20stitchTiles='stitch'/%3E%3C/filter%3E%3Crect%20width='100%25'%20height='100%25'%20filter='url(%23n)'/%3E%3C/svg%3E";

const GRAIN_KEYFRAMES = `@keyframes scrollyGrain {
  0% { background-position: 0px 0px; }
  25% { background-position: -32px 20px; }
  50% { background-position: 24px -28px; }
  75% { background-position: -18px -12px; }
  100% { background-position: 0px 0px; }
}`;

export function GlobalLookOverlay({ look }: { look: GlobalLook }) {
    if (!look.hatOverlay) return null;
    const { temperature, vignette, grain } = look;
    return (
        <div
            data-testid="global-look-overlay"
            style={{
                position: "absolute",
                inset: 0,
                // Faengt nichts ab — sonst laege eine unsichtbare Scheibe ueber
                // der ganzen Buehne und schluckte jeden Klick.
                pointerEvents: "none",
                overflow: "hidden",
            }}
        >
            {temperature !== 0 && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            temperature > 0 ? "rgb(255,150,40)" : "rgb(40,140,255)",
                        opacity: Math.abs(temperature) * 0.22,
                        mixBlendMode: "soft-light",
                    }}
                />
            )}
            {vignette > 0 && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(ellipse at center, rgba(0,0,0,0) ${Math.max(
                            0,
                            58 - vignette * 46,
                        )}%, rgba(0,0,0,${(vignette * 0.88).toFixed(3)}) 100%)`,
                    }}
                />
            )}
            {grain > 0 && (
                <>
                    <style>{GRAIN_KEYFRAMES}</style>
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            opacity: grain * 0.45,
                            mixBlendMode: "overlay",
                            backgroundImage: `url("${GRAIN_URI}")`,
                            backgroundSize: "160px 160px",
                            animation: "scrollyGrain 0.6s steps(1) infinite",
                        }}
                    />
                </>
            )}
        </div>
    );
}

export default GlobalLookOverlay;
